import React, { useState, useEffect } from 'react';
import {
  Store,
  DollarSign,
  Download,
  TrendingUp,
  Package,
  Eye,
  Star,
  Check,
  AlertCircle,
  Loader2,
  CreditCard,
  Banknote
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

interface SellerSectionProps {
  userId: string;
  saving: boolean;
  onSavingChange: (saving: boolean) => void;
}

interface SellerStats {
  totalBotsPaid: number;
  totalBotsFree: number;
  totalDownloadsPaid: number;
  totalDownloadsFree: number;
  totalRevenue: number;
  totalEarnings: number;
  averageRating: number;
  totalViews: number;
}

interface SellerProfile {
  user_id: string;
  verified: boolean;
  total_sales: number;
  rating: number;
  payout_method: 'bank' | 'paypal' | 'crypto' | '';
  bank_account_name: string | null;
  bank_account_number: string | null;
  bank_name: string | null;
  bank_country: string | null;
  paypal_email: string | null;
  created_at: string;
}

export const SellerSection: React.FC<SellerSectionProps> = ({
  userId,
  saving,
  onSavingChange
}) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<SellerStats>({
    totalBotsPaid: 0,
    totalBotsFree: 0,
    totalDownloadsPaid: 0,
    totalDownloadsFree: 0,
    totalRevenue: 0,
    totalEarnings: 0,
    averageRating: 0,
    totalViews: 0
  });
  const [sellerProfile, setSellerProfile] = useState<SellerProfile | null>(null);
  const [requestingPayout, setRequestingPayout] = useState(false);

  useEffect(() => {
    loadSellerData();
  }, [userId]);

  const loadSellerData = async () => {
    setLoading(true);
    try {
      // Load seller profile
      const { data: profileData, error: profileError } = await supabase
        .from('seller_profiles')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') throw profileError;

      setSellerProfile(profileData || {
        user_id: userId,
        verified: false,
        total_sales: 0,
        rating: 0,
        payout_method: '',
        bank_account_name: null,
        bank_account_number: null,
        bank_name: null,
        bank_country: null,
        paypal_email: null,
        created_at: new Date().toISOString()
      });

      // Load marketplace statistics
      const { data: botsData, error: botsError } = await supabase
        .from('marketplace_bots')
        .select('id, price_usd, purchases_count, views_count, rating_avg')
        .eq('seller_id', userId)
        .eq('status', 'active');

      if (botsError) throw botsError;

      // Calculate stats
      const paidBots = (botsData || []).filter(bot => bot.price_usd > 0);
      const freeBots = (botsData || []).filter(bot => bot.price_usd === 0);

      const totalDownloadsPaid = paidBots.reduce((sum, bot) => sum + (bot.purchases_count || 0), 0);
      const totalDownloadsFree = freeBots.reduce((sum, bot) => sum + (bot.purchases_count || 0), 0);
      const totalRevenue = paidBots.reduce((sum, bot) => sum + (bot.price_usd * (bot.purchases_count || 0)), 0);
      const totalEarnings = totalRevenue * 0.7; // 70% commission (30% platform fee)
      const totalViews = (botsData || []).reduce((sum, bot) => sum + (bot.views_count || 0), 0);

      const ratingsSum = (botsData || []).reduce((sum, bot) => sum + (bot.rating_avg || 0), 0);
      const averageRating = (botsData || []).length > 0 ? ratingsSum / (botsData || []).length : 0;

      setStats({
        totalBotsPaid: paidBots.length,
        totalBotsFree: freeBots.length,
        totalDownloadsPaid,
        totalDownloadsFree,
        totalRevenue,
        totalEarnings,
        averageRating,
        totalViews
      });
    } catch (error: any) {
      console.error('Error loading seller data:', error);
      toast.error('Failed to load seller data');
    } finally {
      setLoading(false);
    }
  };

  const saveSellerProfile = async () => {
    if (!sellerProfile) return;

    onSavingChange(true);
    try {
      const { error } = await supabase
        .from('seller_profiles')
        .upsert({
          user_id: userId,
          payout_method: sellerProfile.payout_method,
          bank_account_name: sellerProfile.bank_account_name,
          bank_account_number: sellerProfile.bank_account_number,
          bank_name: sellerProfile.bank_name,
          bank_country: sellerProfile.bank_country,
          paypal_email: sellerProfile.paypal_email,
        });

      if (error) throw error;

      toast.success('Seller profile updated successfully');
    } catch (error: any) {
      console.error('Error saving seller profile:', error);
      toast.error(error?.message || 'Failed to save seller profile');
    } finally {
      onSavingChange(false);
    }
  };

  const requestPayout = async () => {
    if (stats.totalEarnings < 100) {
      toast.error('Minimum payout amount is $100');
      return;
    }

    if (!sellerProfile?.payout_method) {
      toast.error('Please configure payout method first');
      return;
    }

    setRequestingPayout(true);
    try {
      // TODO: Implement payout request logic
      // Create payout_requests table entry
      toast.success('Payout request submitted! We will process it within 3-5 business days.');
    } catch (error: any) {
      console.error('Error requesting payout:', error);
      toast.error('Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Seller Dashboard</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage your marketplace listings and earnings
          </p>
        </div>
        {sellerProfile?.verified && (
          <span className="px-3 py-1 bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full text-sm font-semibold flex items-center gap-1">
            <Check className="h-4 w-4" />
            Verified Seller
          </span>
        )}
      </div>

      {/* Earnings Summary */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <DollarSign className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm opacity-90">Total Earnings (70% commission)</p>
              <p className="text-3xl font-bold">${stats.totalEarnings.toFixed(2)}</p>
            </div>
          </div>
          <button
            onClick={requestPayout}
            disabled={requestingPayout || stats.totalEarnings < 100}
            className="px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center gap-2 transition-colors"
          >
            {requestingPayout ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Banknote className="h-5 w-5" />
                Request Payout
              </>
            )}
          </button>
        </div>
        <div className="flex items-center gap-4 text-sm opacity-90">
          <span>Total Revenue: ${stats.totalRevenue.toFixed(2)}</span>
          <span>•</span>
          <span>Platform Fee (30%): ${(stats.totalRevenue * 0.3).toFixed(2)}</span>
          <span>•</span>
          <span>Min. Payout: $100</span>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Paid Bots */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Package className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Paid</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalBotsPaid}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Paid Bots Listed</p>
        </div>

        {/* Free Bots */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Package className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Free</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalBotsFree}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Free Bots Listed</p>
        </div>

        {/* Paid Downloads */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Download className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Paid</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalDownloadsPaid}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Paid Bot Sales</p>
        </div>

        {/* Free Downloads */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <Download className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">Free</span>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalDownloadsFree}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Free Bot Downloads</p>
        </div>

        {/* Total Views */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <Eye className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalViews.toLocaleString()}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Views</p>
        </div>

        {/* Average Rating */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Star className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.averageRating.toFixed(1)}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Average Rating</p>
        </div>

        {/* Conversion Rate */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <TrendingUp className="h-5 w-5 text-teal-600 dark:text-teal-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {stats.totalViews > 0 ? ((stats.totalDownloadsPaid / stats.totalViews) * 100).toFixed(1) : '0.0'}%
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Conversion Rate</p>
        </div>

        {/* Total Sales */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{sellerProfile?.total_sales || 0}</p>
          <p className="text-sm text-gray-600 dark:text-gray-400">Lifetime Sales</p>
        </div>
      </div>

      {/* Payout Information */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-blue-600 rounded-lg">
            <CreditCard className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Payout Settings</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Configure how you receive payments</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Payout Method
            </label>
            <select
              value={sellerProfile?.payout_method || ''}
              onChange={(e) => setSellerProfile(sellerProfile ? { ...sellerProfile, payout_method: e.target.value as any } : null)}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select payout method</option>
              <option value="bank">Bank Transfer (Recommended)</option>
              <option value="paypal">PayPal</option>
              <option value="crypto">Cryptocurrency</option>
            </select>
          </div>

          {sellerProfile?.payout_method === 'bank' && (
            <div className="space-y-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Account Holder Name
                  </label>
                  <input
                    type="text"
                    value={sellerProfile.bank_account_name || ''}
                    onChange={(e) => setSellerProfile({ ...sellerProfile, bank_account_name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Account Number / IBAN
                  </label>
                  <input
                    type="text"
                    value={sellerProfile.bank_account_number || ''}
                    onChange={(e) => setSellerProfile({ ...sellerProfile, bank_account_number: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="DE89370400440532013000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    value={sellerProfile.bank_name || ''}
                    onChange={(e) => setSellerProfile({ ...sellerProfile, bank_name: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Deutsche Bank"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Bank Country
                  </label>
                  <input
                    type="text"
                    value={sellerProfile.bank_country || ''}
                    onChange={(e) => setSellerProfile({ ...sellerProfile, bank_country: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Germany"
                  />
                </div>
              </div>
            </div>
          )}

          {sellerProfile?.payout_method === 'paypal' && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                PayPal Email
              </label>
              <input
                type="email"
                value={sellerProfile.paypal_email || ''}
                onChange={(e) => setSellerProfile({ ...sellerProfile, paypal_email: e.target.value })}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your@paypal.com"
              />
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-200">
                <p className="font-medium mb-2">Payout Information:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Minimum payout amount: $100</li>
                  <li>Platform fee: 30% (You receive 70%)</li>
                  <li>Payout processing time: 3-5 business days</li>
                  <li>Your payment details are encrypted and secure</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={saveSellerProfile}
            disabled={saving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Save Payout Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
